import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { MAX_GENERATORI, TIPI_GENERATORE, type Generatore, type TipoGeneratore } from '@emacomfort/domain';
import { useDevice, useStato } from '@/app/DeviceProvider';
import { useAvvisi } from '@/app/useAvvisi';
import { Avviso, Badge, Bottone, Campo, Card, Interruttore, Pagina, Select, stili } from '@/ui';
import { IconaGeneratore } from '@/ui/icone';
import { useTecnico } from '../comune';
import s from '../features.module.css';

export function GeneratoriPage() {
  const { t } = useTranslation();
  const { esegui } = useTecnico();
  const { store, client } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const attuatori = useStato((st) => st.attuatori);
  const mostra = useAvvisi((a) => a.mostra);
  const [lista, setLista] = useState<Generatore[] | null>(null);
  const [originale, setOriginale] = useState('');

  useEffect(() => {
    void esegui((tec, sess) => tec.getGeneratori(sess)).then((g) => {
      if (g) {
        const ordinati = [...g].sort((a, b) => a.priorita - b.priorita);
        setLista(ordinati);
        setOriginale(JSON.stringify(ordinati));
      }
    });
  }, [esegui]);

  if (!impianto || !lista) return null;
  const ingressi = impianto.canali.filter((c) => c.direzione === 'INPUT' && c.grandezza === 'TEMPERATURA');
  const uscite = impianto.canali.filter((c) => c.direzione === 'OUTPUT');
  const cambiato = JSON.stringify(lista) !== originale;

  const aggiorna = (i: number, patch: Partial<Generatore>) => setLista(lista.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const sposta = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= lista.length) return;
    const copia = [...lista];
    [copia[i], copia[j]] = [copia[j]!, copia[i]!];
    setLista(copia.map((g, k) => ({ ...g, priorita: k + 1 })));
  };
  const salva = async () => {
    const ok = await esegui(async (tec, sess) => {
      await tec.putGeneratori(sess, lista.map((g, k) => ({ ...g, priorita: k + 1 })));
      return true;
    });
    if (ok) {
      setOriginale(JSON.stringify(lista));
      store.setState({ impianto: await client.getImpianto() });
      mostra(t('tecnico.generatori.salvato'), 'ok');
    }
  };

  return (
    <Pagina
      titolo={t('tecnico.generatori.titolo')}
      sottotitolo={t('tecnico.generatori.sottotitolo')}
      azione={
        <Bottone variante="primario" disabled={!cambiato || lista.length === 0} onClick={() => void salva()}>
          {t('tecnico.generatori.salva')}
        </Bottone>
      }
    >
      <Avviso tono="info">{t('tecnico.generatori.cascataNota')}</Avviso>
      {lista.map((g, i) => {
        const attivo = g.canaleComando ? attuatori[g.canaleComando]?.stato === 'attivo' : false;
        return (
          <Card key={g.id}>
            <div className={s.generatoreRiga}>
              <div className={s.priorita}>
                <Bottone quadrato aria-label={t('tecnico.generatori.su')} onClick={() => sposta(i, -1)} disabled={i === 0}>
                  <ArrowUp size={26} aria-hidden />
                </Bottone>
                <span className={s.prioritaNumero}>{i + 1}</span>
                <Bottone quadrato aria-label={t('tecnico.generatori.giu')} onClick={() => sposta(i, 1)} disabled={i === lista.length - 1}>
                  <ArrowDown size={26} aria-hidden />
                </Bottone>
              </div>
              <div className={stili.griglia}>
                <div className={stili.riga}>
                  <IconaGeneratore tipo={g.tipo} size={28} aria-hidden />
                  <span className={stili.valoreTerziario}>{g.nome}</span>
                  <Badge tono={attivo ? 'ok' : 'neutro'}>{attivo ? t('tecnico.generatori.attivo') : t('tecnico.generatori.inattivo')}</Badge>
                  <span className={stili.spazio} />
                  <Bottone variante="pericolo" onClick={() => setLista(lista.filter((_, j) => j !== i))} disabled={lista.length <= 1}>
                    <Trash2 size={22} aria-hidden />
                    {t('tecnico.generatori.rimuovi')}
                  </Bottone>
                </div>
                <div className={s.campiGriglia}>
                  <Campo etichetta={t('tecnico.generatori.nome')}>
                    <input className={stili.input} value={g.nome} onChange={(e) => aggiorna(i, { nome: e.target.value })} />
                  </Campo>
                  <Campo etichetta={t('tecnico.generatori.tipo')}>
                    <Select value={g.tipo} onChange={(e) => aggiorna(i, { tipo: e.target.value as TipoGeneratore })}>
                      {TIPI_GENERATORE.map((tg) => (
                        <option key={tg} value={tg}>
                          {t(`generatore.${tg}`)}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                  <Campo etichetta={t('tecnico.generatori.canaleMandata')}>
                    <Select value={g.canaleMandata} onChange={(e) => aggiorna(i, { canaleMandata: e.target.value })}>
                      {ingressi.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                  <Campo etichetta={t('tecnico.generatori.canaleRitorno')}>
                    <Select value={g.canaleRitorno} onChange={(e) => aggiorna(i, { canaleRitorno: e.target.value })}>
                      {ingressi.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                  <Campo etichetta={t('tecnico.generatori.canaleComando')}>
                    <Select value={g.canaleComando ?? ''} onChange={(e) => aggiorna(i, { canaleComando: e.target.value || undefined })}>
                      <option value="">{t('tecnico.generatori.nessunComando')}</option>
                      {uscite.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id}
                        </option>
                      ))}
                    </Select>
                  </Campo>
                  <div className={stili.campo}>
                    <span className={stili.etichetta}>{t('tecnico.generatori.modulante')}</span>
                    <Interruttore acceso={g.modulante} onChange={(v) => aggiorna(i, { modulante: v })} etichetta={t('tecnico.generatori.modulante')} testoAcceso={t('generatore.modulante')} testoSpento={t('generatore.onOff')} />
                  </div>
                </div>
              </div>
            </div>
          </Card>
        );
      })}
      <div className={stili.riga}>
        <Bottone
          disabled={lista.length >= MAX_GENERATORI}
          onClick={() =>
            setLista([
              ...lista,
              {
                id: `g-${Date.now().toString(36)}`,
                nome: t('generatore.CALDAIA_GAS'),
                tipo: 'CALDAIA_GAS',
                canaleMandata: ingressi[0]?.id ?? '',
                canaleRitorno: ingressi[1]?.id ?? '',
                priorita: lista.length + 1,
                modulante: false,
              },
            ])
          }
        >
          <Plus size={22} aria-hidden />
          {t('tecnico.generatori.aggiungi')}
        </Bottone>
        <span className={s.letturaNota}>{t('tecnico.generatori.massimo')}</span>
      </div>
    </Pagina>
  );
}
